import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import * as authApi from '../api/auth';
import type { SessionResponse } from '../api/auth';
import { setSessionExpiredHandler } from '../api/client';
import { fetchMyPermissions } from './permissions';

/** Người dùng đang đăng nhập. */
export interface SignedInUser {
  readonly userId: string;
  readonly userName: string;
  readonly fullName: string;
  readonly companyId: string;
  readonly mustChangePassword: boolean;
}

/** Trạng thái xác thực của ứng dụng. */
export interface AuthState {
  /**
   * Đang thử phục hồi phiên từ cookie.
   *
   * Trong lúc này chưa biết người dùng đã đăng nhập hay chưa, nên KHÔNG được hiển thị trang
   * đăng nhập — làm vậy sẽ khiến trang đăng nhập nhấp nháy một nhịp rồi biến mất, với người
   * dùng thì trông như hệ thống bị lỗi.
   */
  readonly isRestoring: boolean;

  readonly user: SignedInUser | null;

  /**
   * Khai dạng thuộc tính hàm chứ không dạng phương thức.
   *
   * Lý do: nơi dùng luôn lấy ra bằng phép tách đối tượng, và một phương thức bị tách khỏi đối tượng
   * của nó sẽ mất tham chiếu tới chính đối tượng đó. Khai dạng thuộc tính hàm làm điều đó không
   * xảy ra được, và bộ kiểm tra mã nguồn cũng thôi cảnh báo.
   */
  readonly signIn: (userName: string, password: string) => Promise<void>;

  readonly signOut: () => Promise<void>;

  readonly changePassword: (currentPassword: string, newPassword: string) => Promise<void>;

  /**
   * Người dùng có một quyền cụ thể hay không.
   *
   * Dùng để ẩn hiện mục thực đơn và nút bấm. KHÔNG phải một lớp bảo vệ: máy chủ kiểm tra lại quyền
   * ở từng điểm cuối, vì bất cứ thứ gì giao diện gửi lên cũng có thể bị sửa. Ẩn một nút chỉ để
   * người dùng không bấm vào thứ họ không làm được.
   *
   * Trả về false trong lúc danh sách quyền chưa nạp xong. Mặc định là từ chối, không phải cho phép:
   * một nút hiện ra rồi biến mất khó hiểu hơn một nút xuất hiện muộn một nhịp.
   */
  readonly hasPermission: (permissionCode: string) => boolean;

  /**
   * Người dùng thấy được toàn bộ dữ liệu của pháp nhân đang làm việc.
   *
   * Đây là <b>lớp thứ ba</b> của phân quyền — phạm vi theo dòng dữ liệu — chứ không phải quyền chức
   * năng. Dùng để ẩn những thao tác mà máy chủ chắc chắn từ chối với người bị giới hạn phạm vi, ví dụ
   * di chuyển khách hàng trong cây: thao tác đó viết lại cả nhánh bên dưới nên nó đòi phạm vi toàn
   * pháp nhân. Hiện nút rồi trả 403 thì người dùng không hiểu vì sao.
   *
   * Trả về false trong lúc chưa nạp xong, cùng lý do như phép kiểm quyền: mặc định là từ chối.
   */
  readonly hasCompanyWideDataScope: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export { AuthContext };

/** Đổi phản hồi phiên thành thông tin người dùng. */
function toUser(session: SessionResponse): SignedInUser {
  return {
    userId: session.userId,
    userName: session.userName,
    fullName: session.fullName,
    companyId: session.companyId,
    mustChangePassword: session.mustChangePassword,
  };
}

/** Cung cấp trạng thái xác thực cho toàn bộ ứng dụng. */
export function AuthProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [user, setUser] = useState<SignedInUser | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);

  /** Rỗng nghĩa là chưa nạp xong, khác với tập rỗng nghĩa là không có quyền nào. */
  const [permissionCodes, setPermissionCodes] = useState<ReadonlySet<string> | null>(null);

  /** Mặc định là false: chưa biết thì coi như bị giới hạn phạm vi. */
  const [hasCompanyWideDataScope, setHasCompanyWideDataScope] = useState(false);

  useEffect(() => {
    // Dùng bộ điều khiển hủy thay vì một biến cờ: đây là cách chuẩn để bỏ kết quả của một lời gọi
    // không đồng bộ khi thành phần đã bị tháo khỏi cây. Không có bước này thì React cảnh báo
    // về việc đặt trạng thái cho thành phần không còn tồn tại.
    const controller = new AbortController();

    void (async () => {
      const session = await authApi.restoreSession();

      if (controller.signal.aborted) {
        return;
      }

      setUser(session === null ? null : toUser(session));
      setIsRestoring(false);
    })();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    // Khi việc tự làm mới phiên thất bại, tầng gọi API báo về đây để đưa người dùng ra
    // trang đăng nhập. Không tự chuyển trang từ tầng gọi API: nó không biết gì về điều hướng.
    setSessionExpiredHandler(() => {
      setUser(null);
      setPermissionCodes(null);
    });
  }, []);

  useEffect(() => {
    // Nạp quyền sau khi đã biết người dùng là ai. Chưa đăng nhập thì không gọi: lời gọi sẽ nhận
    // mã 401 và làm tầng gọi API thử làm mới phiên một cách vô ích.
    if (user === null) {
      return undefined;
    }

    const controller = new AbortController();

    void (async () => {
      try {
        const permissions = await fetchMyPermissions();

        if (!controller.signal.aborted) {
          setPermissionCodes(new Set(permissions.permissionCodes));
          setHasCompanyWideDataScope(permissions.hasCompanyWideDataScope);
        }
      } catch {
        // Không nạp được quyền thì giữ nguyên trạng thái chưa biết, tức là không có quyền nào.
        // Đoán rằng người dùng có quyền khi chưa biết sẽ hiện ra những nút bấm vào là lỗi.
        if (!controller.signal.aborted) {
          setPermissionCodes(new Set());
          setHasCompanyWideDataScope(false);
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [user]);

  const signIn = useCallback(async (userName: string, password: string) => {
    const session = await authApi.signIn(userName, password);

    setUser(toUser(session));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authApi.signOut();
    } finally {
      setUser(null);
      setPermissionCodes(null);
    }
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const session = await authApi.changePassword(currentPassword, newPassword);

    // Cập nhật người dùng từ phiên mới. Bước này quan trọng vì nó xóa dấu "phải đổi mật khẩu" —
    // nếu giữ nguyên trạng thái cũ thì người dùng vừa đổi xong vẫn bị giữ ở màn hình đổi mật khẩu.
    setUser(toUser(session));
  }, []);

  const hasPermission = useCallback(
    (permissionCode: string) => permissionCodes?.has(permissionCode) ?? false,
    [permissionCodes],
  );

  const value = useMemo<AuthState>(
    () => ({
      isRestoring,
      user,
      signIn,
      signOut,
      changePassword,
      hasPermission,
      hasCompanyWideDataScope,
    }),
    [
      isRestoring,
      user,
      signIn,
      signOut,
      changePassword,
      hasPermission,
      hasCompanyWideDataScope,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
